import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Check, Circle, ShieldCheck, Fingerprint, Copy, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PdfPreview } from "./PdfPreview";
import { OpsPanel } from "./OpsPanel";
import { fmtDate, fmtDateTime, fmtMoney, typeLabel, shortHash } from "@/lib/contracts";
import { cn } from "@/lib/utils";
import { useLang, translateFr as tfr } from "@/i18n";
import { toast } from "sonner";

const Card = ({ title, children, testid, right }) => (
  <section className="rounded-2xl border border-border bg-surface p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">{title}</h2>
      {right}
    </div>
    {children}
  </section>
);

const InfoRow = ({ label, value, testid }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-border last:border-0">
    <span className="text-sm text-muted-foreground shrink-0">{label}</span>
    <span className="text-sm text-right font-medium" data-testid={testid}>{value ?? "—"}</span>
  </div>
);

const WorkflowTimeline = ({ workflow }) => (
  <ol className="relative" data-testid="workflow-timeline">
    {workflow.map((step, i) => {
      const last = i === workflow.length - 1;
      return (
        <motion.li key={step.key} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.25, delay: i * 0.05 }} className="flex gap-3">
          <div className="flex flex-col items-center">
            <div className={cn("h-6 w-6 rounded-full flex items-center justify-center border shrink-0",
              step.done ? "bg-green-500/15 border-green-500/40 text-green-500"
                : step.current ? "bg-amber-500/15 border-amber-500/40 text-amber-400"
                  : "bg-secondary/30 border-border text-muted-foreground")}>
              {step.done ? <Check className="h-3.5 w-3.5" /> : <Circle className="h-2 w-2 fill-current" />}
            </div>
            {!last && <div className={cn("w-px flex-1 min-h-[18px]", step.done ? "bg-green-500/30" : "bg-secondary/50")} />}
          </div>
          <p className={cn("text-sm pb-4", step.done ? "text-foreground" : step.current ? "text-amber-400" : "text-muted-foreground")}>
            {tfr(step.label)}
            {step.current && <span className="ml-2 text-xs rounded-full bg-amber-500/10 border border-amber-500/25 px-2 py-0.5">{tfr("En cours")}</span>}
          </p>
        </motion.li>
      );
    })}
  </ol>
);

const SignatureSummaryCard = ({ contract }) => {
  const { tf } = useLang();
  const navigate = useNavigate();
  const sig = contract.signature;
  if (!sig) return null;
  const p = sig.progress || { signed: 0, total: 0 };
  const goSig = () => navigate(`/contracts/${contract.id}?tab=signatories`);
  let headline, sub, action, actionLabel;
  if (["sent", "in_progress"].includes(sig.status)) {
    headline = tf("En signature");
    sub = `${p.signed} ${tf("personne(s) sur")} ${p.total} ${tf("ont signé.")}`;
    action = goSig; actionLabel = tf("Relancer un signataire");
  } else if (sig.status === "completed" && contract.status === "upcoming") {
    headline = tf("Signé");
    sub = `${tf("Prend effet le")} ${contract.start_date ? fmtDate(contract.start_date) : "—"}.`;
    action = () => navigate(`/contracts/${contract.id}?tab=documents`); actionLabel = tf("Voir le contrat signé");
  } else if (sig.status === "completed") {
    headline = tf("Signé");
    sub = tf("Toutes les signatures ont été obtenues.");
    action = () => navigate(`/contracts/${contract.id}?tab=documents`); actionLabel = tf("Voir le contrat signé");
  } else if (["ready", "draft"].includes(sig.status)) {
    headline = tf("Signature en préparation");
    sub = sig.status === "ready" ? tf("Prêt à envoyer pour signature.") : tf("Document final et signataires à configurer.");
    action = goSig; actionLabel = sig.status === "ready" ? tf("Finaliser") : tf("Voir la signature");
  } else {
    headline = tf("Signature interrompue");
    sub = tf("La dernière procédure n'a pas abouti.");
    action = goSig; actionLabel = tf("Voir la signature");
  }
  return (
    <Card title="Signature" testid="details-signature-card" right={<PenLine className="h-4 w-4 text-muted-foreground" />}>
      <p className="text-sm font-semibold" data-testid="signature-summary-headline">{headline}</p>
      <p className="text-xs text-muted-foreground mt-1" data-testid="signature-summary-sub">{sub}</p>
      {p.total > 0 && ["sent", "in_progress", "completed"].includes(sig.status) && (
        <div className="h-1.5 rounded-full bg-secondary/50 mt-3 overflow-hidden">
          <div className="h-full rounded-full bg-green-500" style={{ width: `${p.percent || 0}%` }} />
        </div>
      )}
      <Button size="sm" variant="outline" className="mt-3 w-full" onClick={action} data-testid="signature-summary-action">
        {actionLabel}
      </Button>
    </Card>
  );
};

export const TabDetails = ({ contract, onChanged }) => {
  const primaryDoc = contract.documents?.find((d) => d.id === contract.primary_document_id) || null;
  const cert = contract.certification || {};
  const copyHash = () => { navigator.clipboard.writeText(cert.sha256); toast.success(tfr("Empreinte SHA-256 copiée")); };

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="tab-details">
      <div className="lg:col-span-2 space-y-4">
        {["active", "upcoming"].includes(contract.status) && <OpsPanel contract={contract} onChanged={onChanged} />}
        <Card title={tfr("Document principal")} testid="details-pdf-card">
          <PdfPreview cid={contract.id} doc={primaryDoc} />
        </Card>
      </div>
      <div className="space-y-4">
        <SignatureSummaryCard contract={contract} />
        <Card title={tfr("Avancement")} testid="details-workflow-card">
          <WorkflowTimeline workflow={contract.workflow || []} />
        </Card>
        <Card title={tfr("Informations générales")} testid="details-info-card">
          <InfoRow label="Type" value={typeLabel(contract.contract_type)} testid="info-type" />
          <InfoRow label={tfr("Athlète")} value={contract.player_name} testid="info-player" />
          <InfoRow label={tfr("Contrepartie")} value={contract.counterparty} testid="info-counterparty" />
          <InfoRow label={tfr("Début")} value={contract.start_date ? fmtDate(contract.start_date) : null} testid="info-start" />
          <InfoRow label={tfr("Fin")} value={contract.end_date ? fmtDate(contract.end_date) : null} testid="info-end" />
          <InfoRow label={tfr("Montant")} value={fmtMoney(contract.amount_cents ?? (contract.value_amount != null ? contract.value_amount * 100 : null), contract.currency)} testid="info-amount" />
          <InfoRow label="Commission" value={contract.commission_percent != null ? `${contract.commission_percent} %` : null} testid="info-commission" />
          <InfoRow label={tfr("Créé le")} value={fmtDateTime(contract.created_at)} testid="info-created" />
          {contract.description && (
            <p className="text-sm text-muted-foreground mt-3 whitespace-pre-wrap" data-testid="info-description">{contract.description}</p>
          )}
        </Card>
        <Card title={tfr("Certification & intégrité")} testid="details-cert-card"
          right={<ShieldCheck className="h-4 w-4 text-green-500" />}>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Fingerprint className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{tfr("Empreinte SHA-256 du document principal")}</p>
                {cert.sha256 ? (
                  <div className="flex items-center gap-1.5">
                    <code className="text-xs font-mono break-all" data-testid="cert-hash">{shortHash(cert.sha256)}</code>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={copyHash} data-testid="cert-hash-copy"><Copy className="h-3 w-3" /></Button>
                  </div>
                ) : <p className="text-sm">—</p>}
              </div>
            </div>
            <InfoRow label={tfr("Horodatage")} value={cert.timestamp ? fmtDateTime(cert.timestamp) : null} testid="cert-timestamp" />
            <InfoRow label={tfr("Événements d'audit")} value={cert.audit_events} testid="cert-events" />
            <InfoRow label="Signature" value={tfr("Suivi manuel")} testid="cert-provider" />
            <p className="text-xs text-muted-foreground leading-relaxed border-t border-border pt-3" data-testid="cert-note">
              {cert.note}
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};
